import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect
} from "react";
import cn from "classnames";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import cloneDeep from "lodash/cloneDeep";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useFormik } from "formik";
import map from "lodash/map";
import { useHistory } from "react-router-dom";

import { saveToSessionStorage, getFromSessionStorage } from "@/util/storage";

import {
  months,
  years,
  daysInMonth,
  generateBirhdayString
} from "@/util/birthday";
import { useCountryShort, useIsRTL, useLanguage, useRouteSlug } from "@/hooks";

import CustomDropdown from "../customDropdown";
import IntlInput from "../intlTelInput/intlTelInput";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  selectCountryData,
  selectMobileLocalNumberLength,
  selectRegistrationDataStatus,
  selectSettings,
  selectShowLoader
} from "@/util/selectors";
import { buildDynamicFormConfig } from "./dynamicFieldsConfig";
import {
  handleMandatoryFields,
  initiateMobileVerification
} from "@/redux/actions/homepage.action";
import { getCountriesList } from "@/constants/nationality";
import { DRAFT_STORAGE_KEY, SIGNUP_API_CODE_NAMES } from "@/constants";
import { ButtonWithLoader } from "../common/buttons";
import { getDividedNumber } from "@/util/phoneNumber";
import {
  getCountryList,
  setUserOriginCountry
} from "@/redux/actions/common.action";
import { convertToLabelValue, getAcceptablePhoneLengths } from "@/util";
import DialogModal from "../dialogModal";
import { resetResponseUpdateDetailsData } from "@/redux/actions/authConfig.action";
import { DynamicUpdateProfileFields } from "./types";
import "./style.scss";
import { TurnstileCaptcha, TurnstileCaptchaRef } from "../captcha/captcha";

type Props = {
  isModalShow?: boolean;
  setUpdateDetailsModal?: React.Dispatch<React.SetStateAction<boolean>>;
  data?: DynamicUpdateProfileFields;
  token?: string;
  updateAction?: ({
    bodyData,
    token
  }: {
    bodyData?: any;
    token?: string;
  }) => (dispatch: any, getState: any) => Promise<any>;
  otpAction?: ({
    token,
    otp,
    country,
    language,
    history
  }) => (dispatch: any, getState: any) => Promise<any>;
  loginOptionalOptions?: {
    onAuth?: (response: any) => void;
    withCartRedirect?: boolean;
    redirect?: boolean;
  };
  userProfile?: {
    phone?: string;
    email?: string;
    firstname?: string;
    lastname?: string;
    birthday?: string;
    nationality?: string;
    countryId?: number;
  };
  onModalClose?: () => void;
  className?: string;
  isSignUpFlow?: boolean;
  isSignIn?: boolean;
  isSubmitting?: boolean;
  socialFlow?: boolean;
  otpCallback?: any;
  isUserUpdateFlow?: boolean;
  enableDraft?: boolean;
};

type OtpArgs = {
  token: string;
  otp: string;
  country: string;
  language: string;
  history: History;
};

const UpdateDetailsComponent: React.FC<Props> = ({
  data = {},
  token,
  updateAction = handleMandatoryFields,
  otpCallback,
  loginOptionalOptions,
  userProfile,
  className,
  isSignUpFlow = false,
  isSubmitting = false,
  socialFlow = false,
  isUserUpdateFlow = false,
  isSignIn = false,
  enableDraft = false
}) => {
  const initialDraft = enableDraft
    ? getFromSessionStorage(DRAFT_STORAGE_KEY)
    : null;

  const [selectedBirthdayData, setSelectedBirthdayData] = useState(
    initialDraft?.birthdayData || {
      selectedDay: "",
      selectedMonth: null,
      selectedYear: null
    }
  );

  const [showConflictMobileModal, setShowConflictMobileModal] = useState(false);
  const [isConflictMobileFlow, setIsConflictMobileFlow] = useState(false);
  const [tokenForOtp, setTokenForOtp] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [isInteractiveCaptcha, setIsInteractiveCaptcha] = useState(false);
  const [updateErrorMessage, setUpdateErrorMessage] = useState("");
  const formikPhoneInputRef = useRef(null);
  const isRTL = useIsRTL();
  const { t } = useTranslation(["signUp", "myAccount", "auth"]);
  const language = useLanguage();
  const history = useHistory();
  const countrySlug = useCountryShort();
  const countryData = useAppSelector(selectCountryData);
  const showLoader = useAppSelector(selectShowLoader);
  const routeSlug = useRouteSlug();
  const mobileLocalNumberLength = useAppSelector(selectMobileLocalNumberLength);
  const registrationDataStatus = useAppSelector(selectRegistrationDataStatus);
  const turnstileCaptchaToken = useAppSelector(
    state => state.authReducer.turnstileCaptchaToken
  );
  const settings = useAppSelector(selectSettings);
  const valueCaptchaFromCMS = settings?.captcha?.userRegisterScreen;
  const captchaRef = useRef<TurnstileCaptchaRef>(null);
  const isAutomaticCaptcha =
    !captchaLoading &&
    !isInteractiveCaptcha &&
    valueCaptchaFromCMS &&
    !turnstileCaptchaToken;
  const shouldShowLoader = isAutomaticCaptcha || captchaLoading;

  const userOriginCountryData = useAppSelector(
    state => state.common.userOriginCountryData
  );
  const selectedCountryMobileDetails = useAppSelector(
    state => state.common.selectedCountryMobileDetails
  );
  const defaultAreaCode =
    selectedCountryMobileDetails?.mobileLocalCode?.split(",");
  const mobileCountryCode = selectedCountryMobileDetails?.mobileCountryCode;

  const dispatch = useAppDispatch();

  const dividedPhone =
    userProfile.phone &&
    getDividedNumber({
      mobileCountryCode: selectedCountryMobileDetails.mobileCountryCode,
      mobileLocalCode: selectedCountryMobileDetails.mobileLocalCode,
      mobileLocalNumberLength: Math.max(
        ...getAcceptablePhoneLengths(selectedCountryMobileDetails)
      ),
      phone: userProfile.phone
    });

  const config = buildDynamicFormConfig(
    {
      ...data,
      acceptablePhoneLengths: getAcceptablePhoneLengths(
        selectedCountryMobileDetails
      ),
      ...(userProfile.phone &&
        selectedCountryMobileDetails.mobileCountryCode && {
          initialPhone: dividedPhone.phoneWithoutCountryCode.slice(
            dividedPhone.areaCode.length
          ),
          initialArea: dividedPhone.areaCode,
          initialCountryCode: selectedCountryMobileDetails.mobileCountryCode
        })
    },
    t
  );

  const initialValuesTemplate: Record<string, any> = {};
  const validationShape: Record<string, Yup.AnySchema> = {};

  Object.entries(config).forEach(([key, field]) => {
    if (field.enabled) {
      initialValuesTemplate[key] = field.initialValue;
      if (field.mandatory) {
        validationShape[key] = field.validation;
      }
    }
  });

  const redirect = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const checkoutRedirect = searchParams.get("redirect");
    return checkoutRedirect;
  }, [location.search]);

  const [countries, localizeCountries] = useMemo(() => {
    const countries = getCountriesList(false).map(country => ({
      value: country.code,
      label: country.name
    }));

    const localizeCountries = getCountriesList(isRTL).map(country => ({
      value: country.code,
      label: country.name
    }));
    return [countries, localizeCountries];
  }, [isRTL]);

  const getPhoneNumberParts = useCallback(
    ({ phone, areaCode: phoneArea }) => {
      const areaCode = phoneArea || get(defaultAreaCode, "[0]", "");
      return { mobileCountryCode, areaCode, phone };
    },
    [mobileCountryCode, defaultAreaCode]
  );

  const formik = useFormik({
    initialValues: initialDraft?.formikValues
      ? { ...initialValuesTemplate, ...initialDraft.formikValues }
      : initialValuesTemplate,
    validationSchema: Yup.object(validationShape),
    onSubmit: async values => {
      if (!isEmpty(values)) {
        await handleUpdateAction(values);
      }
    }
  });

  const {
    values,
    handleChange,
    handleSubmit,
    setFieldValue,
    touched,
    errors,
    submitCount
  } = formik;

  useEffect(() => {
    if (enableDraft) {
      const draftData = {
        formikValues: cloneDeep(values),
        birthdayData: selectedBirthdayData
      };

      saveToSessionStorage(DRAFT_STORAGE_KEY, draftData);
    }
  }, [values, selectedBirthdayData, enableDraft]);

  const handleUpdateAction = async formValues => {
    const {
      areaCode,
      isAgeVerified,
      isNewsLetter,
      isLoyaltyProgramChecked,
      countryCode,
      nationality,
      ...others
    } = formValues;
    setUpdateErrorMessage("");

    const birthday = generateBirhdayString(selectedBirthdayData, language);
    const response = await dispatch(
      updateAction({
        bodyData: {
          ...others,
          ...((socialFlow || isSignUpFlow) &&
            !isUserUpdateFlow && { phone: getFullPhone() }),
          ...(data?.birthday && birthday
            ? {
                birthday
              }
            : {}),
          ...(data?.nationality && nationality
            ? {
                nationalityCountryCode2: nationality,
                nationality: countries.find(item => item.value === nationality)
                  ?.label
              }
            : {})
        },
        token,
        loginSuccessData: {
          history,
          language,
          country: countrySlug,
          ...(loginOptionalOptions ? loginOptionalOptions : {})
        }
      })
    );

    if (response instanceof Error) {
      setUpdateErrorMessage(
        //@ts-ignore
        response.response?.data?.message ||
          response.message ||
          "Something went wrong during the update"
      );
      if (
        //@ts-ignore
        response.response?.data?.code ===
        SIGNUP_API_CODE_NAMES.MOBILE_ALREADY_USED_IN_ANOTHER_ACCOUNT
      ) {
        setShowConflictMobileModal(true);
        setIsConflictMobileFlow(true);
        //@ts-ignore
        setTokenForOtp(response.response?.data?.token);
      }
      return;
    }

    if (response.code === SIGNUP_API_CODE_NAMES.OTP_REQUIRED) {
      otpCallback?.(
        response.token,
        getFullPhone(),
        getStructuredPhoneWithSpaces()
      );
      setTokenForOtp(response.token);
    }
  };

  useEffect(() => {
    if (
      userProfile.phone &&
      selectedCountryMobileDetails.countryId === userProfile.countryId
    ) {
      setFieldValue(
        "phone",
        dividedPhone.phoneWithoutCountryCode.slice(dividedPhone.areaCode.length)
      );
      setFieldValue("areaCode", dividedPhone.areaCode);
    }
  }, [userProfile.phone, selectedCountryMobileDetails.countryId]);

  useEffect(() => {
    if (userProfile.countryId) {
      const manageUserOriginCountry = async () => {
        const updatedUserInfo = {
          ...userProfile,
          countryId: userProfile.countryId
        };
        if (updatedUserInfo.countryId) {
          try {
            if (countryData.length <= 1) {
              await dispatch(getCountryList(language));
            } else {
              const userOriginCountry = countryData.find(
                item => item.countryId === updatedUserInfo.countryId
              );
              if (userOriginCountry) {
                dispatch(setUserOriginCountry(userOriginCountry));
              }
            }
          } catch (error) {
            console.error("Error setting user origin country:", error);
          }
        }
      };
      manageUserOriginCountry();
    }
  }, [userProfile.countryId, countryData]);

  useEffect(() => {
    if (
      registrationDataStatus &&
      registrationDataStatus.code !==
        SIGNUP_API_CODE_NAMES.MOBILE_ALREADY_USED_IN_ANOTHER_ACCOUNT &&
      !registrationDataStatus.success &&
      registrationDataStatus.message
    ) {
      setUpdateErrorMessage(registrationDataStatus.message);

      setTimeout(() => {
        setUpdateErrorMessage("");
      }, 5000);
    }
  }, [registrationDataStatus]);

  const handleUpdateMobile = async () => {
    const response = await dispatch(initiateMobileVerification(tokenForOtp));

    if (response.code === SIGNUP_API_CODE_NAMES.OTP_REQUIRED) {
      setTokenForOtp(response.token);
    }
    setShowConflictMobileModal(false);
    if (!response.response?.data?.success && response.response?.data?.message) {
      setIsConflictMobileFlow(false);
      setUpdateErrorMessage(
        //@ts-ignore
        response.response?.data?.message ||
          response.message ||
          "Something went wrong during the update"
      );
    }
  };

  const handleSkip = () => {
    dispatch(resetResponseUpdateDetailsData());
    history.push(`/${routeSlug}/${redirect ?? ""}`);
  };

  const getFullPhone = () => {
    //@ts-ignore
    const { mobileCountryCode, areaCode, phone } = getPhoneNumberParts(values);
    return `${mobileCountryCode}${areaCode}${phone}`;
  };

  const getStructuredPhoneWithSpaces = () => {
    //@ts-ignore
    const { mobileCountryCode, areaCode, phone } = getPhoneNumberParts(values);
    return `${mobileCountryCode} ${areaCode} ${phone}`;
  };
  const countryBasedOnKey = localizeCountries.find(
    item => item.value === values.nationality
  );

  const isInputInvalid = (field: string) =>
    (touched[field] || submitCount > 0) && Boolean(errors[field]);

  const fieldsToTrack = useMemo(() => Object.keys(data || {}), [data]);

  const isButtonDisabled = useMemo(() => {
    if (fieldsToTrack.length === 0) {
      return true;
    }

    const isAtLeastOneFieldFilled = fieldsToTrack.some(fieldKey => {
      if (fieldKey === "birthday") {
        const { selectedDay, selectedMonth, selectedYear } =
          selectedBirthdayData;
        return !!(selectedDay && selectedMonth && selectedYear);
      }
      return !isEmpty(values[fieldKey]);
    });

    return !isAtLeastOneFieldFilled;
  }, [fieldsToTrack, selectedBirthdayData, values]);

  return (
    <>
      <DialogModal
        classname="mobile_conflict_modal"
        bodyText={updateErrorMessage}
        cancelButtonText={t("signUp:cancel")}
        confirmButtonText={t("signUp:update")}
        showModal={showConflictMobileModal}
        onCancel={() => setShowConflictMobileModal(false)}
        onConfirm={handleUpdateMobile}
      />

      <form
        onSubmit={handleSubmit}
        className={cn("signUp update_modal_layout", className)}
      >
        {data?.fullname && (
          <div className="form-group">
            <input
              type="text"
              name="fullname"
              placeholder={t("signUp:name")}
              className={cn("form-control", {
                borderRed: isInputInvalid("fullname")
              })}
              value={values.fullname || ""}
              onChange={handleChange}
            />
            {isInputInvalid("fullname") && (
              <span className="error">
                {typeof errors.fullname === "string"
                  ? errors.fullname
                  : t("signUp:enterName")}
              </span>
            )}
          </div>
        )}
        {data?.firstname && (
          <div className="form-group">
            <input
              type="text"
              name="firstname"
              placeholder={t("signUp:firstName")}
              className={cn("form-control", {
                borderRed: isInputInvalid("firstname")
              })}
              value={values.firstname || ""}
              onChange={handleChange}
            />
            {isInputInvalid("firstname") && (
              <span className="error">{t("signUp:enterFirstName")}</span>
            )}
          </div>
        )}
        {data?.email && (
          <div className="form-group">
            <input
              type="email"
              name="email"
              placeholder={t("signUp:email")}
              className={cn("form-control", {
                borderRed: isInputInvalid("email")
              })}
              value={values.email || ""}
              onChange={handleChange}
            />
            {isInputInvalid("email") && (
              <span className="error">{t("signUp:emailRequired")}</span>
            )}
          </div>
        )}

        {data?.nationality && (
          <div className="form-group">
            <CustomDropdown
              data={localizeCountries}
              selectedItem={
                countryBasedOnKey || convertToLabelValue(t("auth:nationality"))
              }
              containerClass={cn("location_dropdown nationality", {
                placeholder: !countryBasedOnKey
              })}
              handleChange={({ value }) => {
                setFieldValue("nationality", value);
              }}
              withSearchInput
            />
            {isInputInvalid("nationality") && (
              <span className="error">{t("signUp:required")}</span>
            )}
          </div>
        )}

        {data?.birthday && (
          <div className="birthday">
            <h4 id="date-of-birth" className="birthdayTitle">
              {t("auth:dateOfBirth")}{" "}
              <span>{t("auth:dateOfBirthSecondPart")}</span>
            </h4>
            <div className="selects-block-wrapper">
              <div className="selects-block">
                <CustomDropdown
                  data={convertToLabelValue(daysInMonth)}
                  selectedItem={convertToLabelValue(
                    selectedBirthdayData.selectedDay || t("auth:dd")
                  )}
                  containerClass={cn("location_dropdown day", {
                    placeholder: !selectedBirthdayData.selectedDay
                  })}
                  scrollbarMaxHeight={100}
                  handleChange={({ value }) => {
                    setSelectedBirthdayData(prev => ({
                      ...prev,
                      selectedDay: value as any
                    }));
                  }}
                />
                <CustomDropdown
                  data={convertToLabelValue(map(months, language))}
                  selectedItem={convertToLabelValue(
                    selectedBirthdayData.selectedMonth || t("auth:mm")
                  )}
                  containerClass={cn("location_dropdown month", {
                    placeholder: !selectedBirthdayData.selectedMonth
                  })}
                  scrollbarMaxHeight={100}
                  handleChange={({ value }) => {
                    setSelectedBirthdayData(prev => ({
                      ...prev,
                      selectedMonth: value
                    }));
                  }}
                />
                <CustomDropdown
                  id="year"
                  data={convertToLabelValue(years)}
                  selectedItem={convertToLabelValue(
                    selectedBirthdayData.selectedYear || t("auth:yy")
                  )}
                  containerClass={cn("location_dropdown year", {
                    placeholder: !selectedBirthdayData.selectedYear
                  })}
                  scrollbarMaxHeight={100}
                  handleChange={({ value }) => {
                    setSelectedBirthdayData(prev => ({
                      ...prev,
                      selectedYear: value
                    }));
                  }}
                />
              </div>
            </div>
            <p className="surprise">{t("auth:birthdaySurprise")}</p>
          </div>
        )}

        {data?.gender && (
          <div className="radio_group flex gender">
            <h4>{t("auth:gender")}</h4>
            <p>
              <input
                name="gender"
                type="radio"
                id="maleRadioInput"
                className={cn("custom_radio", {
                  checked_radio: values.gender === "Male"
                })}
                value="Male"
                checked={values.gender === "Male"}
                onChange={handleChange}
              />
              <label htmlFor="maleRadioInput">{t("signUp:male")}</label>
            </p>
            <p>
              <input
                name="gender"
                type="radio"
                id="femaleRadioInput"
                className={cn("custom_radio", {
                  checked_radio: values.gender === "Female"
                })}
                value="Female"
                checked={values.gender === "Female"}
                onChange={handleChange}
              />
              <label htmlFor="femaleRadioInput">{t("signUp:female")}</label>
            </p>
            {isInputInvalid("gender") && (
              <span className="error">{t("signUp:required")}</span>
            )}
          </div>
        )}

        {data?.phone && (
          <div
            className={cn("form-group", {
              errorBorder: isInputInvalid("phone")
            })}
          >
            <IntlInput
              valid={true}
              phone={values.phone || ""}
              areaCode={values.areaCode || ""}
              handleCountryChange={(e, phone) => setFieldValue("phone", phone)}
              onSelectArea={area => {
                if (area !== values.areaCode) {
                  setFieldValue("phone", "");
                  setFieldValue("areaCode", area);
                }
              }}
              defaultCountry={userOriginCountryData.countrySHORT}
              countryData={countryData}
              forwardedRef={formikPhoneInputRef}
              mobileLocalNumberLength={mobileLocalNumberLength}
              onSelectFlag={(_, flagData) => {
                setFieldValue("countryCode", flagData.dialCode);
              }}
              withCountrySelection
              withoutCountryDropDown
              clearPhoneInput={() => setFieldValue("phone", "")}
            />
            {isInputInvalid("phone") && (
              <span className="error">{t("signUp:phoneRequired")}</span>
            )}
          </div>
        )}

        {values.hasOwnProperty("isAgeVerified") && (
          <div className="form-group checkbox_wrap square_chkBox default_address age_verification">
            <input
              type="checkbox"
              name="isAgeVerified"
              id="isAgeVerified"
              className="custom_checkbox"
              checked={values.isAgeVerified || false}
              onChange={handleChange}
            />
            <label htmlFor="isAgeVerified" className="checkbox_value">
              {t("myAccount:verifiedBirthday")}
            </label>
            {isInputInvalid("isAgeVerified") && (
              <span className="error">{t("signUp:required")}</span>
            )}
          </div>
        )}

        {isSignUpFlow && !isConflictMobileFlow && updateErrorMessage && (
          <div className="form-error">
            <span className="error">{updateErrorMessage}</span>
          </div>
        )}

        {valueCaptchaFromCMS && (
          <TurnstileCaptcha
            ref={captchaRef}
            className="mt-4 mb-4"
            beforeLoading={() => setCaptchaLoading(true)}
            afterLoading={() => {
              setCaptchaLoading(false);
            }}
            onInterectiveChallangeStarted={() => {
              setIsInteractiveCaptcha(true);
            }}
            onToken={() => setCaptchaLoading(false)}
            onTokenExpired={() => setCaptchaLoading(false)}
          />
        )}

        {isUserUpdateFlow && (
          <div className="flex actionButtons">
            <ButtonWithLoader
              type="submit"
              className="btn btn-default round_btn form_black_btn saveButton"
              showLoader={isSubmitting || shouldShowLoader}
              disabled={isSubmitting || isButtonDisabled || shouldShowLoader}
              showLoaderWithoutClick={true}
            >
              {t("auth:save")}
            </ButtonWithLoader>
            <button type="button" className="skipButton" onClick={handleSkip}>
              {t("auth:skip")}
            </button>
          </div>
        )}

        {isSignUpFlow && socialFlow && !isUserUpdateFlow && (
          <ButtonWithLoader
            disabled={showLoader || shouldShowLoader}
            showLoaderWithoutClick={true}
            showLoader={showLoader || shouldShowLoader}
            type="submit"
            className="btn btn-default round_btn form_black_btn"
          >
            {t("myAccount:update")}
          </ButtonWithLoader>
        )}

        {isSignUpFlow && socialFlow && !isSignIn && !isUserUpdateFlow && (
          <div className="form-group checkbox_wrap square_chkBox default_address loyalty_program">
            <input
              type="checkbox"
              name="isLoyaltyProgramChecked"
              id="isLoyaltyProgramChecked"
              className="custom_checkbox"
              checked={values.isLoyaltyProgramChecked || false}
              onChange={handleChange}
            />
            <label htmlFor="isLoyaltyProgramChecked" className="checkbox_value">
              {t("myAccount:loyaltyProgramSignUp")}
            </label>
            {isInputInvalid("isLoyaltyProgramChecked") && (
              <span className="error">{t("signUp:required")}</span>
            )}
          </div>
        )}

        {!socialFlow && (
          <>
            <div className="form-group checkbox_wrap square_chkBox default_address">
              <input
                type="checkbox"
                name="isLoyaltyProgramChecked"
                id="isLoyaltyProgramChecked"
                className="custom_checkbox"
                checked={values.isLoyaltyProgramChecked || false}
                onChange={handleChange}
              />
              <label
                htmlFor="isLoyaltyProgramChecked"
                className="checkbox_value"
              >
                {t("myAccount:loyaltyProgramSignUp")}
              </label>
              {isInputInvalid("isLoyaltyProgramChecked") && (
                <span className="error">{t("signUp:required")}</span>
              )}
            </div>

            <div className="form-group checkbox_wrap square_chkBox default_address">
              <input
                type="checkbox"
                name="isNewsLetter"
                id="isNewsLetter"
                className="custom_checkbox"
                checked={values.isNewsLetter || false}
                onChange={handleChange}
              />
              <label htmlFor="isNewsLetter" className="checkbox_value">
                {t("myAccount:subscribeForNewsLetter")}
              </label>
              {isInputInvalid("isNewsLetter") && (
                <span className="error">{t("signUp:required")}</span>
              )}
            </div>
          </>
        )}

        {!isSignUpFlow && !isConflictMobileFlow && updateErrorMessage && (
          <div className="form-error">
            <span className="error">{updateErrorMessage}</span>
          </div>
        )}

        {!isSignUpFlow && (
          <ButtonWithLoader
            showLoader={showLoader}
            type="submit"
            className="btn btn-default round_btn form_black_btn"
          >
            {t("myAccount:update")}
          </ButtonWithLoader>
        )}
      </form>
    </>
  );
};

export default UpdateDetailsComponent;
